import React from "react";
import { useToggleFilter } from "@dxp-feed/algolia";
import AllergensView from "./AllergensView";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@dxp-feed/ui/Accordion";
import { useAppSelector, useEffect, useRef, useState } from "@/utils/hooks/reactUseCustomHooks";
import { algoliaAnalyticsKind } from "@/services/algolia/analytics";
import { ALGOLIA_INDEXES } from "@/services/algolia/config";
import useFilterViewAnalytics from "@/utils/hooks/useFilterViewAnalytics";

const AllergensFilter = () => {
  const allergenContainerRef = useRef<HTMLDivElement>(null);
  const { Allergens } = useAppSelector((state) => state.getProductAemDataSlice);
  const [isOpen, setIsOpen] = useState(false);
  const [allergensList, setAllergensList] = useState<
    { attribute: string; name: string }[]
  >([]);

  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    if (
      Allergens?.allergensOptions &&
      Object.keys(Allergens.allergensOptions).length > 0
    ) {
      const options = Allergens.allergensOptions as Record<string, string>;
      const list = Object.entries(options)
        .map(([attribute, name]) => ({ attribute, name }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setAllergensList(list);
    }
  }, [Allergens]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      try {
        const header = document.querySelector(`#item-${Allergens?.title}`);
        if (header) {
          const isExpanded = header.getAttribute("aria-expanded") === "true";
          setIsOpen(isExpanded);
        }
      } catch (error) {
        console.error(error)
      }
    });

    try {
      const header = document.querySelector(`#item-${Allergens?.title}`);
      if (header) {
        observer.observe(header, {
          attributes: true,
          attributeFilter: ["aria-expanded"],
        });
  
        // Set initial state
        const isExpanded = header.getAttribute("aria-expanded") === "true";
        setIsOpen(isExpanded);
      }
    } catch (error) {
      console.error(error)
    }

    return () => {
      observer.disconnect();
    };
  }, [Allergens?.title]);

  const handleVisibilityChange = (attribute: string, isVisible: boolean) => {
    setVisibilityMap((prev) => ({
      ...prev,
      [attribute]: isVisible,
    }));
  };

  const allergensFilterStatus = Object.values(visibilityMap).some((v) => v);

  const userSession = useAppSelector((state) => state.gigyaslice);
  const userId = userSession.uid ?? undefined;

  const { itemRefs } = useFilterViewAnalytics({
    items: allergensList,
    containerRef: allergenContainerRef,
    analyticsPayload: {
      kind: algoliaAnalyticsKind.PRODUCTS,
      index: ALGOLIA_INDEXES.PRODUCTS.PRIMARY_INDEX,
      userToken: userId,
    },
    filterStringCb: (idx: number) => `${allergensList[idx].attribute}:${true}`,
  });

  return (
    <Accordion
      className={`flex-col gap-6 lg:gap-4 border-b border-grey350 ${allergensFilterStatus ? "flex" : "hidden"}`}
      multiplePanelOpen={true}
    >
      <AccordionItem
        id={`item-${Allergens?.title}`}
        key={`itemAllergens-${Allergens?.title}`}
      >
        <AccordionHeader
          id={`item-${Allergens?.title}`}
          className="font-bold !px-0 pt-0 pb-2 text-[16px] lg:text-[14px] group [&_.demo-icon]:w-6 [&_.demo-icon]:h-6 [&_.demo-icon]:flex [&_.demo-icon]:items-center [&_.demo-icon]:justify-center"
        >
          {Allergens?.title}
        </AccordionHeader>
        <AccordionPanel
          id={`item-${Allergens?.title}`}
          customRef={allergenContainerRef}
          className="[&>div]:p-0 [&>div]:flex [&>div]:flex-col [&>div]:gap-2 [&>div]:py-0 [&>div]:pb-2 lg:[&>div]:py-2 [&>div]:border-t [&>div]:border-none lg:[&>div]:border-grey350 [&>div]:overflow-x-hidden [&>div]:overflow-y-auto [&>div]:max-h-[280px] [&>div]:scroll-bar-grey"
        >
          {/* {allergensList.map((allergen, index) => ( */}
          {allergensList?.map((allergen, index) => (
            <AllergensView
              key={allergen.attribute}
              attribute={allergen.attribute}
              name={allergen.name}
              index={index}
              isParentOpen={isOpen}
              onVisibilityChange={(visible) => {
                handleVisibilityChange(allergen.attribute, visible);
              }}
              ref={(el) => (itemRefs.current[index] = el)}
            />
          ))}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default AllergensFilter;
